/**
 * Register Page - Authority self-registration
 *
 * Allows authorities (Behörden) to create their own encryption key pair
 * and download a PDF backup. Optionally collects additional profile information.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Button, Typography, Link } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { KeySetupWizard, StoredKeyPair, ProfileData, useCryptoKeyStore, generateKeyId } from '../features/encryption';
import { registerAuthorityExtended, createAuthorityWithKey } from '../features/encryption/api/keysApi';

export function RegisterPage() {
  const navigate = useNavigate();
  const { initializeAuthority } = useCryptoKeyStore();

  // Handle registration complete
  const handleComplete = useCallback(
    async (keyPair: StoredKeyPair, profileData?: ProfileData) => {
      try {
        // Register the public key with the backend (with optional profile data)
        await registerAuthorityExtended({
          ownerName: keyPair.ownerName ?? 'Behörde',
          publicKeyJwk: keyPair.publicKeyJwk,
          fingerprint: keyPair.fingerprint,
          profile: profileData ? {
            organizationName: profileData.organizationName,
            department: profileData.department,
            contactEmail: profileData.contactEmail,
            streetAddress: profileData.streetAddress,
            postalCode: profileData.postalCode,
            city: profileData.city,
            phone: profileData.phone,
            website: profileData.website,
          } : undefined,
        });

        // Also create an authority with GKDK slot so user can create processes
        const authorityId = generateKeyId();
        const authorityName = profileData?.organizationName || keyPair.ownerName || 'Meine Behörde';

        // Initialize authority locally (creates AEK + GKDK slot)
        const bundle = await initializeAuthority(authorityId, authorityName);

        // Register authority with backend
        await createAuthorityWithKey({
          authorityId,
          authorityName,
          publicKeyJwk: bundle.aekPublicKeyJwk,
          fingerprint: bundle.aekFingerprint,
          creatorKeyId: keyPair.keyId,
          creatorName: keyPair.ownerName || 'Unbekannt',
          creatorPublicKeyJwk: keyPair.publicKeyJwk,
          creatorFingerprint: keyPair.fingerprint,
        });
      } catch (error) {
        // If registration fails, the user can still use the key locally
        // In production, you might want to handle this differently
        console.warn('Failed to register key with backend:', error);
      }

      // Navigate to dashboard
      navigate('/', { replace: true });
    },
    [navigate, initializeAuthority]
  );

  // Handle cancel
  const handleCancel = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          py: 4,
        }}
      >
        {/* Back button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/login')}
          sx={{ alignSelf: 'flex-start', mb: 2 }}
        >
          Zurück zur Anmeldung
        </Button>

        {/* Wizard */}
        <KeySetupWizard
          ownerType="AUTHORITY"
          onComplete={handleComplete}
          onCancel={handleCancel}
          title="Als Behörde registrieren"
          subtitle="Erstellen Sie Ihren behördlichen Zugangsschlüssel"
        />

        {/* Info text */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Sie haben bereits einen Schlüssel?{' '}
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/login')}
            >
              Zur Anmeldung
            </Link>
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}
